import React, { useState, useEffect, useRef, useMemo } from 'react';
import { toast } from 'components/ui/toast/use-toast';
import { cn } from 'utils/helpers';
import { Contact } from 'modules/profile/types/types';
import { getMyContacts } from 'utils/helpers';
import Button from 'components/v2/button/Button';
import EmojiAvatar from 'modules/home/pages/homePage/EmojiAvatar';
import { Check } from 'lucide-react';

type Props = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onAddContacts: () => void;
  eventContacts: Contact[];
  selectedContacts: Contact[];
  setSelectedContacts: React.Dispatch<React.SetStateAction<Contact[]>>;
};

const AddContactsPopup: React.FC<Props> = ({
  isOpen,
  setIsOpen,
  onAddContacts,
  selectedContacts,
  setSelectedContacts,
  eventContacts,
}) => {
  const [fetchedContacts, setFetchedContacts] = useState<Contact[]>([]);
  const popupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchContacts = async () => {
      try {
        const contactsData = await getMyContacts();
        if (Array.isArray(contactsData)) {
          setFetchedContacts(contactsData);
        }
      } catch (error) {
        console.error('Error fetching contacts:', error);
      }
    };

    if (isOpen) {
      fetchContacts();
    }
  }, [isOpen]);

  const handleClickOutside = (event: MouseEvent) => {
    if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
      setIsOpen(false);
      setSelectedContacts([]);
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleContactClick = (contact: Contact) => {
    setSelectedContacts((prev) => {
      if (
        prev.some((selected) => selected.phoneNumber === contact.phoneNumber)
      ) {
        return prev.filter(
          (selected) => selected.phoneNumber !== contact.phoneNumber,
        );
      } else {
        return [...prev, contact];
      }
    });
  };

  const sortedContacts = useMemo(() => {
    return [...fetchedContacts].sort((a, b) => a.name.localeCompare(b.name));
  }, [fetchedContacts, selectedContacts]);

  return (
    <div
      className={cn(
        'w-full fixed inset-0 z-50 flex items-end justify-center bg-black bg-opacity-50 transition-opacity duration-500',
        isOpen
          ? 'opacity-100 pointer-events-auto'
          : 'opacity-0 pointer-events-none',
      )}
    >
      <div
        ref={popupRef}
        className={cn(
          'w-full bg-main p-6 rounded-t-lg shadow-lg transition-transform duration-300 transform',
          isOpen ? 'translate-y-0' : 'translate-y-full',
        )}
      >
        <p className="text-[1.5rem] font-manrope font-semibold mb-4">
          {selectedContacts.length > 0 ? 'Add more friends' : 'Add a contact'}
        </p>
        <div className="mb-4 overflow-y-scroll max-h-[20rem]">
          <div className="flex flex-col gap-2">
            {sortedContacts.map((contact, index) => {
              const isSelected = selectedContacts.some(
                (selected) => selected.phoneNumber === contact.phoneNumber,
              );
              const isEventContact = eventContacts.some(
                (eventContact) =>
                  eventContact.phoneNumber === contact.phoneNumber,
              );
              return (
                <div
                  key={index}
                  onClick={() => {
                    if (isEventContact) {
                      return;
                    }
                    handleContactClick(contact);
                  }}
                  className={cn(
                    'flex items-center justify-between gap-4 px-5 py-4 rounded-[1rem] cursor-pointer',
                    isSelected ? 'bg-[#2A2A4D]' : 'bg-[#110E2B]',
                    isEventContact && 'opacity-50 cursor-not-allowed',
                  )}
                >
                  <div className="flex items-center gap-4 w-full">
                    <div className="relative">
                      <EmojiAvatar
                        fullName={contact.name}
                        image={`${process.env.REACT_APP_BASE_URL}/users/profile-pic/${contact.userId}`}
                      />
                    </div>
                    <div className="flex flex-col justify-center items-start">
                      <div className="flex gap-1 text-xs font-semibold text-center truncate contact-search-name">
                        <div className="text-[0.875rem] font-semibold">
                          {contact.name.split(' ')[0]}
                        </div>
                      </div>
                      <div className="text-[0.875rem] text-[rgb(255,255,255,0.8)] font-normal text-center">
                        {contact.name}
                      </div>
                    </div>{' '}
                  </div>
                  {(isSelected || isEventContact) && (
                    <Check className="text-[#ABE49C] w-4 h-4" />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex justify-center gap-5">
          <Button
            onClick={() => {
              onAddContacts();
              setIsOpen(false);
              setSelectedContacts([]);
            }}
            disabled={selectedContacts.length === 0}
            className="p-3"
          >
            Add
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              setIsOpen(false);
              setSelectedContacts([]);
            }}
            className="p-3"
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddContactsPopup;
